import { forwardRef, useCallback, useState } from "react";
import { Clickable } from "reakit/Clickable";
import { omit } from "lodash";
import classNames from "classnames";

import { useButtonProps } from "./useButtonProps";
import { assignForwardedRef } from "../Utils/assignForwardedRef";
import { useRefocusOnNextRender } from "../Utils/useRefocusOnNextRender";

const Button = (props, providedRef) => {
  const { disabled, refocusAfterRender } = props;
  const [buttonNode, setButtonNode] = useState();

  const {
    passthroughProps: inhertiedPassthroughProps,
    className: inheritedClassName,
    content
  } = useButtonProps(props);

  const passthroughProps = omit(
    inhertiedPassthroughProps,
    "disabled",
    disabled && "onClick",
    disabled && "type",
    "refocusAfterRender"
  );

  const buttonElementType = disabled ? "div" : "button";
  const className = classNames(inheritedClassName, { disabled });

  const ref = useCallback(
    element => {
      assignForwardedRef(providedRef, element);
      setButtonNode(element);
    },
    [providedRef]
  );

  const refocusOnNextRender = useRefocusOnNextRender(buttonNode);
  const { onClick } = passthroughProps;

  const handleClick = useCallback(
    e => {
      // This allows buttons that act as links and are re-rendered after navigation to be
      // refocused when the rendering of the new screen is complete, which enhances
      // keyboard navigation and is an accessibility requirement.
      if (refocusAfterRender) refocusOnNextRender();

      if (onClick) onClick(e);
    },
    [onClick, refocusAfterRender, refocusOnNextRender]
  );

  return (
    <Clickable
      {...passthroughProps}
      as={buttonElementType}
      ref={ref}
      disabled={disabled}
      className={className}
      onClick={handleClick}
    >
      {content}
    </Clickable>
  );
};

export default forwardRef(Button);
